/**
 * P2P Save Helper Utilities
 * Extracted complex validation and payload building logic
 * Reduces cognitive complexity in save handlers
 */

import type {
  ActionItemData,
  AssessmentData,
  P2PSavePayload,
  CategoryLevelsResponse,
  TeamSettingsPayload,
  PerformanceData,
} from "../../store/slices/pathToPerformanceSlice";
import { validateActionItems, calculateP2PRating } from "./p2pValidation";
import { mapActionItemsToPayload } from "./p2pPayloadMapper";
import { convertDateToYYYYMMDD, parseFlexibleDateToMMDDYYYY } from "../date";
import { 
  HUDDLES_OPTIONS, 
  ERROR_MESSAGES, 
  SUCCESS_MESSAGES,
  ASSESSMENT_PERIODS,
} from "../../constants/pathToPerformance";

/**
 * Validation result type
 */
export interface ValidationResult {
  isValid: boolean;
  error: string | null;
  isNoChanges?: boolean;
}

/**
 * Validate P2P save operation
 * Checks for changes and validates action items
 * 
 * @param actionItems - Current action items
 * @param hasChanges - Whether data has changed from initial state
 * @returns Validation result with error message if invalid
 * 
 * @example
 * ```typescript
 * const validation = validateP2PSave(actionItems, hasChanges());
 * if (!validation.isValid) {
 *   handleValidationError(validation);
 * }
 * ```
 */
export const validateP2PSave = (
  actionItems: ActionItemData[],
  hasChanges: boolean
): ValidationResult => {
  // Check if there are any changes
  if (!hasChanges) {
    return {
      isValid: false,
      error: SUCCESS_MESSAGES.NO_CHANGES,
      isNoChanges: true,
    };
  }

  // Validate action items - ensure at least one is selected
  if (!validateActionItems(actionItems)) {
    return {
      isValid: false,
      error: ERROR_MESSAGES.NO_ACTION_SELECTED,
      isNoChanges: false,
    };
  }

  return { isValid: true, error: null };
};

/**
 * Build complete P2P save payload
 * Combines action items, assessments, and calculated rating
 * 
 * @param teamId - Team ID
 * @param actionItems - Current action items
 * @param assessments - Current assessments
 * @param categoryLevels - Category levels from API
 * @returns Complete P2P save payload
 * 
 * @example
 * ```typescript
 * const payload = buildP2PSavePayload(teamId, actionItems, assessments, categoryLevels);
 * await dispatch(saveP2PData({ data: payload, isUpdate }));
 * ```
 */
export const buildP2PSavePayload = (
  teamId: number,
  actionItems: ActionItemData[],
  assessments: AssessmentData[],
  categoryLevels: CategoryLevelsResponse | null
): P2PSavePayload => {
  // Calculate p2pRating from action items
  const p2pRating = calculateP2PRating(actionItems);

  // Build action item payload using utility function
  const actionItemPayload = mapActionItemsToPayload(actionItems, categoryLevels);

  // Get current assessment notes
  const currentAssessment = assessments.find(
    (a) => a.period.toLowerCase() === ASSESSMENT_PERIODS.CURRENT.toLowerCase()
  );

  return {
    teamId,
    p2pRating,
    p2pAssesmentNotes: currentAssessment?.assessmentNotes ?? null,
    ...actionItemPayload,
  };
};

/**
 * Validate performance table date field
 * Checks date format and converts to API format
 * 
 * @param dateValue - Date string to validate
 * @returns Validation result with converted date
 */
export interface DateValidationResult extends ValidationResult {
  convertedDate?: string | null;
}

/**
 * Build team settings payload from performance data
 * Transforms UI state to API format
 *
 * Rules for `learningPlanRefreshDate`:
 * - If it was **empty on initial load** and the user leaves it empty → the field is
 *   omitted from the payload entirely (not a required field in this case).
 * - If it was **populated on initial load** and the user clears it → validation fails
 *   with a "required" error (mandatory when originally set).
 * - If a value is present → the date format is validated and the field is included.
 *
 * @param teamId - Team ID
 * @param performanceData - Performance table data
 * @param validateDate - Date validation function
 * @param initialDateValue - The `lastFaceToFaceAssessment` value from the initial (server) load
 * @returns Payload or validation error
 *
 * @example
 * ```typescript
 * const result = buildTeamSettingsPayload(teamId, performanceData, isValidDateFormat, initialDate);
 * if (!result.validation.isValid) {
 *   setValidationErrors([{ field: 'lastFaceToFaceAssessment', message: result.validation.error }]);
 * }
 * ```
 */
export const buildTeamSettingsPayload = (
  teamId: number,
  performanceData: PerformanceData[],
  validateDate: (date: string) => boolean,
  initialDateValue: string = ""
): { payload: TeamSettingsPayload; validation: ValidationResult } => {
  const performanceRow = performanceData[0];
  
  if (!performanceRow) {
    return {
      payload: {} as TeamSettingsPayload,
      validation: {
        isValid: false,
        error: "Performance data not available",
      },
    };
  }

  const dateValue = performanceRow.lastFaceToFaceAssessment?.trim();
  const trimmedInitialDate = initialDateValue.trim();

  // If the field had a value on initial load but the user cleared it, it is required
  if (!dateValue && trimmedInitialDate) {
    return {
      payload: {} as TeamSettingsPayload,
      validation: {
        isValid: false,
        error: ERROR_MESSAGES.LAST_FACE_TO_FACE_REQUIRED,
      },
    };
  }

  // Validate date format when a value is provided
  if (dateValue && !validateDate(dateValue)) {
    return {
      payload: {} as TeamSettingsPayload,
      validation: {
        isValid: false,
        error: ERROR_MESSAGES.INVALID_DATE_FORMAT,
      },
    };
  }

  // Convert huddles value from Yes/No to Y/N
  const usingHuddles = performanceRow.usingHuddles === HUDDLES_OPTIONS.YES 
    ? HUDDLES_OPTIONS.API_YES 
    : HUDDLES_OPTIONS.API_NO;

  // When a date value is provided, convert and include it in the payload
  if (dateValue) {
    // Normalize two-digit years before converting to the API's YYYY-MM-DD format.
    const normalizedDate = parseFlexibleDateToMMDDYYYY(dateValue);
    const learningPlanRefreshDate = normalizedDate
      ? convertDateToYYYYMMDD(normalizedDate)
      : null;

    if (!learningPlanRefreshDate) {
      return {
        payload: {} as TeamSettingsPayload,
        validation: {
          isValid: false,
          error: ERROR_MESSAGES.INVALID_DATE_FORMAT,
        },
      };
    }

    return {
      payload: { teamId, learningPlanRefreshDate, usingHuddles },
      validation: { isValid: true, error: null },
    };
  }

  // Both current and initial dates are empty — omit learningPlanRefreshDate from payload
  return {
    payload: { teamId, usingHuddles },
    validation: { isValid: true, error: null },
  };
};
